/**
 * API Hooks
 *
 * React Query hooks for data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ProductListParams,
  Product,
  ProductFilters,
  RestoreVersionRequest,
  MassEditExecuteRequest,
  MassEditPreviewRequest,
  SubmissionCreate,
  ReviewSubmissionRequest,
  UserUpdate,
  InviteCreate,
  FieldOptionCreateRequest,
  FieldOptionDeprecateRequest,
  FieldOptionFieldKey,
  FieldOptionRenameRequest,
  FallbackInviteCreate,
  FallbackActivateRequest,
  FallbackLoginRequest,
  AuditQueryParams,
} from './types';
import type { BulkFieldImportExecuteRequest, BulkFieldImportExecuteResponse } from './products';
import * as productsApi from './products';
import * as submissionsApi from './submissions';
import * as usersApi from './users';
import * as iriApi from './iri';
import * as imagesApi from './images';
import * as fieldOptionsApi from './field-options';
import * as fallbackAuthApi from './fallback-auth';
import { createHostSessionAdapter } from '../runtime/hostAdapters';
import { getDevGroupedSubmissions } from './dev-grouped-submissions';

const fallbackSession = createHostSessionAdapter();

/**
 * Hook to fetch products with filters and pagination
 */
export const useProducts = (params: ProductListParams = {}) => {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => productsApi.getProducts(params),
    staleTime: 1000 * 60, // 1 minute
  });
};

/**
 * Hook to fetch a single product
 */
export const useProduct = (id: number) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => productsApi.getProduct(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch product image download views
 */
export const useProductImageViews = (productId: number) => {
  return useQuery({
    queryKey: ['product-image-views', productId],
    queryFn: () => productsApi.getProductImageViews(productId),
    enabled: !!productId,
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status;
      return failureCount < 2 && (status === undefined || status >= 500);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchInterval: (query) => {
      if (!query.state.data?.degraded) return false;
      const fetchCount = query.state.dataUpdateCount;
      return fetchCount < 10 ? 3000 : false;
    },
  });
};

export const useExportProductsExcel = () => {
  return useMutation({
    mutationFn: (params: ProductListParams) => productsApi.exportProductsExcel(params),
  });
};

export const useDownloadLatestSppExecutable = () => {
  return useMutation({
    mutationFn: productsApi.downloadLatestSppExecutable,
  });
};

export const useUploadSppExecutable = () => {
  return useMutation({
    mutationFn: ({ file, version }: { file: File; version?: string }) =>
      productsApi.uploadSppExecutable(file, version),
  });
};

export const useStartBulkImageDownload = () => {
  return useMutation({
    mutationFn: (productIds: number[]) => imagesApi.startBulkImageDownload(productIds),
  });
};

export const useBulkImageDownloadProgress = (jobId: string | null, enabled: boolean) => {
  return useQuery({
    queryKey: ['bulk-image-download-progress', jobId],
    queryFn: () => imagesApi.getBulkImageDownloadProgress(jobId || ''),
    enabled: enabled && Boolean(jobId),
  });
};

/**
 * Hook to fetch filter options
 */
export const useFilterOptions = (filters: ProductFilters = {}) => {
  return useQuery({
    queryKey: ['filter-options', filters],
    queryFn: () => productsApi.getFilterOptions(filters),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useProductActivityRollups = (days: number = 30, enabled = true) => {
  return useQuery({
    queryKey: ['product-activity-rollups', days],
    queryFn: () => productsApi.getProductActivityRollups(days),
    staleTime: 1000 * 60,
    enabled,
  });
};

/**
 * Hook to create a product
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Hook to update a product
 */
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Product> }) =>
      productsApi.updateProduct(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
    },
  });
};

export const useDeactivateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.deactivateProduct,
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', product.id] });
      queryClient.invalidateQueries({ queryKey: ['product-history', product.id] });
      queryClient.invalidateQueries({ queryKey: ['product-stats'] });
    },
  });
};

export const usePreviewMassEdit = () => {
  return useMutation({
    mutationFn: (request: MassEditPreviewRequest) => productsApi.previewMassEdit(request),
  });
};

export const useExecuteMassEdit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: MassEditExecuteRequest) => productsApi.executeMassEdit(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-stats'] });
      queryClient.invalidateQueries({ queryKey: ['filter-options'] });
    },
  });
};

export const usePreviewBulkFieldImport = () => {
  return useMutation({
    mutationFn: (file: File) => productsApi.previewBulkFieldImport(file),
  });
};

export const useExecuteBulkFieldImport = () => {
  const queryClient = useQueryClient();

  return useMutation<BulkFieldImportExecuteResponse, Error, BulkFieldImportExecuteRequest>({
    mutationFn: (request) => productsApi.executeBulkFieldImport(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-stats'] });
      queryClient.invalidateQueries({ queryKey: ['filter-options'] });
    },
  });
};

/**
 * Hook to delete a product
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.deleteProduct,
    onSuccess: (_, productId) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['product-history', productId] });
      queryClient.invalidateQueries({ queryKey: ['filter-options'] });
    },
  });
};

/**
 * Hook to reactivate a deactivated product
 */
export const useReactivateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.reactivateProduct,
    onSuccess: (_, productId) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['product-history', productId] });
      queryClient.invalidateQueries({ queryKey: ['filter-options'] });
    },
  });
};

/**
 * Hook to fetch product statistics
 */
export const useProductStats = () => {
  return useQuery({
    queryKey: ['product-stats'],
    queryFn: () => productsApi.getProductStats(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch product version history
 */
export const useProductHistory = (productId: number, limit: number = 50) => {
  return useQuery({
    queryKey: ['product-history', productId, limit],
    queryFn: () => productsApi.getProductHistory(productId, limit),
    enabled: !!productId,
  });
};

/**
 * Hook to fetch product user info (submitted by, updated by)
 */
export const useProductUserInfo = (productId: number) => {
  return useQuery({
    queryKey: ['product-user-info', productId],
    queryFn: () => productsApi.getProductUserInfo(productId),
    enabled: !!productId,
  });
};

/**
 * Hook to restore a product to a previous version
 */
export const useRestoreVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, request }: { productId: number; request: RestoreVersionRequest }) =>
      productsApi.restoreProductVersion(productId, request),
    onSuccess: (_, variables) => {
      // Invalidate product and history queries
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['product-history', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Hook to fetch pending edit submissions for a product
 */
export const usePendingEdits = (productId: number) => {
  return useQuery({
    queryKey: ['pending-edits', productId],
    queryFn: () => productsApi.getPendingEdits(productId),
    enabled: !!productId,
  });
};

/**
 * Hook for fuzzy matching brand names with typo tolerance
 * Returns brands with Levenshtein distance ≤ 3 from the query
 */
export const useFuzzyMatchBrand = (brand: string) => {
  // Ensure brand is a valid non-empty string with at least 2 chars
  const isValidBrand = typeof brand === 'string' && brand.trim().length >= 2;

  return useQuery({
    queryKey: ['fuzzy-match-brand', brand],
    queryFn: () => productsApi.fuzzyMatchBrand(brand),
    enabled: isValidBrand,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook for getting distinct value counts for filter fields
 * Used to determine if async search or static dropdown should be used
 */
export const useFieldCounts = () => {
  return useQuery({
    queryKey: ['field-counts'],
    queryFn: () => productsApi.getFieldCounts(),
    staleTime: 1000 * 60 * 30, // 30 minutes - this rarely changes
  });
};

/**
 * Hook for searching brand names for autocomplete dropdown
 * Returns brands matching the search query
 */
export const useBrandSearch = (query: string, limit: number = 20, filters: ProductFilters = {}) => {
  return useQuery({
    queryKey: ['brand-search', query, limit, filters],
    queryFn: () => productsApi.searchBrands(query, limit, filters),
    enabled: query.length >= 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook for fetching existing brand profile details for auto-fill
 */
export const useBrandProfile = (brand: string) => {
  const normalizedBrand = brand.trim();

  return useQuery({
    queryKey: ['brand-profile', normalizedBrand],
    queryFn: () => productsApi.getBrandProfile(normalizedBrand),
    enabled: normalizedBrand.length >= 1,
    staleTime: 1000 * 60 * 10,
  });
};

/**
 * Hook for searching manufacturer names for autocomplete dropdown
 */
export const useManufacturerSearch = (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
) => {
  return useQuery({
    queryKey: ['manufacturer-search', query, limit, filters],
    queryFn: () => productsApi.searchManufacturers(query, limit, filters),
    enabled: query.length >= 1,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook for searching category names for autocomplete dropdown
 */
export const useCategorySearch = (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
) => {
  return useQuery({
    queryKey: ['category-search', query, limit, filters],
    queryFn: () => productsApi.searchCategories(query, limit, filters),
    enabled: query.length >= 1,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook for searching style names for autocomplete dropdown
 */
export const useStyleSearch = (query: string, limit: number = 20, filters: ProductFilters = {}) => {
  return useQuery({
    queryKey: ['style-search', query, limit, filters],
    queryFn: () => productsApi.searchStyles(query, limit, filters),
    enabled: query.length >= 1,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook for searching package names for autocomplete dropdown
 */
export const usePackageSearch = (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
) => {
  return useQuery({
    queryKey: ['package-search', query, limit, filters],
    queryFn: () => productsApi.searchPackages(query, limit, filters),
    enabled: query.length >= 1,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook for searching size names for autocomplete dropdown
 */
export const useSizeSearch = (query: string, limit: number = 20, filters: ProductFilters = {}) => {
  return useQuery({
    queryKey: ['size-search', query, limit, filters],
    queryFn: () => productsApi.searchSizes(query, limit, filters),
    enabled: query.length >= 1,
    staleTime: 1000 * 60 * 5,
  });
};

// ==================== SUBMISSION HOOKS ====================

/**
 * Hook to lookup UPC for autofill
 */
export const useUPCLookup = (upc: string) => {
  return useQuery({
    queryKey: ['upc-lookup', upc],
    queryFn: () => submissionsApi.lookupUPC(upc),
    enabled: upc.length >= 5, // Only lookup if UPC is at least 5 characters
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useIriValidate = (upc: string, brand: string) => {
  const normalizedUpc = upc.trim();
  const normalizedBrand = brand.trim();
  const enabled = normalizedUpc.length >= 5 && normalizedBrand.length >= 1;

  return useQuery({
    queryKey: ['iri-validate', normalizedUpc, normalizedBrand],
    queryFn: () => iriApi.validateIri(normalizedUpc, normalizedBrand),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch submissions list
 */
export const useSubmissions = (
  status?: string,
  page: number = 1,
  perPage: number = 50,
  source?: 'user' | 'iri'
) => {
  return useQuery({
    queryKey: ['submissions', status, page, perPage, source],
    queryFn: () => submissionsApi.getSubmissions(status, page, perPage, source),
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Hook to fetch grouped submissions with conflict detection
 */
export const useGroupedSubmissions = (
  status?: string,
  page: number = 1,
  perPage: number = 50,
  source?: 'user' | 'iri'
) => {
  return useQuery({
    queryKey: ['grouped-submissions', status, page, perPage, source],
    queryFn: async () => {
      try {
        return await productsApi.getGroupedSubmissions(status, page, perPage, source);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Failed to fetch grouped submissions, using dev mock data', error);
          return getDevGroupedSubmissions(status, page, perPage, source);
        }
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - longer cache since submissions don't change frequently
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes after unused
  });
};

/**
 * Hook to fetch a single submission
 */
export const useSubmission = (id: number) => {
  return useQuery({
    queryKey: ['submission', id],
    queryFn: () => submissionsApi.getSubmission(id),
    enabled: !!id,
  });
};

/**
 * Hook to create a submission
 */
export const useCreateSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubmissionCreate) => submissionsApi.createSubmission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};

/**
 * Hook to get package size suggestion from package string
 */
export const usePackageSizeSuggestion = (packageValue: string) => {
  return useQuery({
    queryKey: ['package-size-suggestion', packageValue],
    queryFn: () => productsApi.getPackageSizeSuggestion(packageValue),
    enabled: packageValue.trim().length >= 3,
    staleTime: 1000 * 60 * 10,
  });
};

/**
 * Hook to get dimension suggestions for a product based on package code
 */
export const useDimensionSuggestions = (productId: number, packageCode: string) => {
  return useQuery({
    queryKey: ['dimension-suggestions', productId, packageCode],
    queryFn: () => productsApi.getDimensionSuggestions(productId, packageCode),
    enabled: productId > 0 && packageCode.trim().length >= 3,
    staleTime: 1000 * 60 * 10,
  });
};

/**
 * Hook to get dimension suggestions by package code (no product ID required)
 */
export const usePackageDimensionSuggestions = (packageCode: string) => {
  return useQuery({
    queryKey: ['package-dimension-suggestions', packageCode],
    queryFn: () => productsApi.getDimensionSuggestionsByPackage(packageCode),
    enabled: packageCode.trim().length >= 3,
    staleTime: 1000 * 60 * 10,
  });
};

export const usePackageAverageDimensions = (packageValue: string) => {
  return useQuery({
    queryKey: ['package-average-dimensions', packageValue],
    queryFn: () => productsApi.getPackageAverageDimensions(packageValue),
    enabled: packageValue.trim().length >= 1,
    staleTime: 1000 * 60 * 10,
  });
};

/**
 * Hook to preview bulk upload file
 */
export const usePreviewBulkUpload = () => {
  return useMutation({
    mutationFn: submissionsApi.previewBulkUpload,
  });
};

export const usePreviewBulkImageUpload = () => {
  return useMutation({
    mutationFn: submissionsApi.previewBulkImageUpload,
  });
};

export const useApplyBulkImageUpload = () => {
  return useMutation({
    mutationFn: ({ files, selectedRows }: { files: File[]; selectedRows: number[] }) =>
      submissionsApi.applyBulkImageUpload(files, selectedRows),
  });
};

export const useStartPreviewBulkUploadJob = () => {
  return useMutation({
    mutationFn: submissionsApi.startPreviewBulkUploadJob,
  });
};

export const usePreviewBulkUploadJobProgress = (jobId: number | null) => {
  return useQuery({
    queryKey: ['bulk-upload-preview-job-progress', jobId],
    queryFn: () => submissionsApi.getPreviewBulkUploadJobProgress(jobId as number),
    enabled: typeof jobId === 'number',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;

      const total = query.state.data?.total ?? 0;
      if (total >= 20_000) return 4000;
      if (total >= 5_000) return 2000;
      return 1000;
    },
  });
};

export const usePreviewBulkUploadJobResult = (jobId: number | null, enabled: boolean) => {
  return useQuery({
    queryKey: ['bulk-upload-preview-job-result', jobId],
    queryFn: () => submissionsApi.getPreviewBulkUploadJobResult(jobId as number),
    enabled: enabled && typeof jobId === 'number',
  });
};

/**
 * Hook to create submissions from bulk upload
 */
export const useCreateBulkUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submissionsApi.createBulkUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
    },
  });
};

/**
 * Hook to download bulk upload template
 */
export const useDownloadBulkTemplate = () => {
  return useMutation({
    mutationFn: submissionsApi.downloadBulkTemplate,
  });
};

/**
 * Hook to review (approve/reject) a submission
 */
export const useReviewSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: ReviewSubmissionRequest }) =>
      submissionsApi.reviewSubmission(id, request),
    onSuccess: (submission, variables) => {
      const productIds = [submission.createdProductId, submission.existingProductId].filter(
        (productId): productId is number => typeof productId === 'number' && productId > 0
      );

      // Invalidate submission queries
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.id] });
      // Also invalidate products since a new one might have been created
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-stats'] });
      queryClient.invalidateQueries({ queryKey: ['product-image-views'] });
      queryClient.invalidateQueries({ queryKey: ['product-view-blob'] });
      queryClient.invalidateQueries({ queryKey: ['product-documents'] });

      productIds.forEach((productId) => {
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
        queryClient.invalidateQueries({ queryKey: ['product-image-views', productId] });
        queryClient.invalidateQueries({ queryKey: ['product-documents', productId] });
      });
    },
  });
};

/**
 * Hook to bulk review submissions with conflict resolution
 */
export const useBulkReviewSubmissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.bulkReviewSubmissions,
    onSuccess: () => {
      // Invalidate all submission-related queries
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
      // Also invalidate products since new ones might have been created/updated
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-stats'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['product-image-views'] });
      queryClient.invalidateQueries({ queryKey: ['product-view-blob'] });
      queryClient.invalidateQueries({ queryKey: ['product-documents'] });
    },
  });
};

/**
 * Hook to add a comment to a submission
 */
export const useAddComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      submissionsApi.addComment(id, { comment }),
    onSuccess: (_, variables) => {
      // Invalidate submission queries to refetch with new comment
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.id] });
    },
  });
};

export const useAdminEditSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: submissionsApi.AdminEditSubmissionRequest;
    }) => submissionsApi.adminEditSubmission(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.id] });
    },
  });
};

export const useSetNeedsInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      submissionsApi.setNeedsInfo(id, message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.id] });
    },
  });
};

export const useSubmitterReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      submissionsApi.submitterReply(id, message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.id] });
    },
  });
};

export const useDeleteAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, attachmentId }: { submissionId: number; attachmentId: number }) =>
      submissionsApi.deleteAttachment(submissionId, attachmentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.submissionId] });
      queryClient.invalidateQueries({ queryKey: ['product-documents'] });
    },
  });
};

export const useReplaceAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      submissionId,
      attachmentId,
      file,
    }: {
      submissionId: number;
      attachmentId: number;
      file: File;
    }) => submissionsApi.replaceAttachment(submissionId, attachmentId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['grouped-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', variables.submissionId] });
      queryClient.invalidateQueries({ queryKey: ['product-documents'] });
    },
  });
};

export const useProductDocuments = (productId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['product-documents', productId],
    queryFn: () => submissionsApi.getProductDocuments(productId),
    enabled: enabled && !!productId,
    staleTime: 1000 * 60 * 5,
  });
};

// ==================== USER MANAGEMENT HOOKS ====================

/**
 * Hook to get current authenticated user
 *
 * Returns the currently logged-in user's information including role and profile.
 * Used for displaying user info and checking permissions client-side.
 *
 * Data is cached for 5 minutes to reduce unnecessary API calls.
 *
 * @example
 * const { data: user, isLoading } = useCurrentUser();
 * if (user?.role === 'admin') { ... }
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: () => usersApi.getCurrentUser(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch paginated users list
 *
 * Admin-only hook. Returns all users with optional filtering.
 * Automatically refetches when filters change.
 *
 * @param page - Page number (default: 1)
 * @param perPage - Items per page (default: 50)
 * @param role - Optional role filter
 * @param search - Optional search by email or name
 *
 * @example
 * const { data: usersData, isLoading } = useUsers(1, 50, 'admin', 'john');
 * const users = usersData?.items || [];
 */
export const useUsers = (
  page: number = 1,
  perPage: number = 50,
  role?: string,
  search?: string
) => {
  return useQuery({
    queryKey: ['users', page, perPage, role, search],
    queryFn: () => usersApi.getUsers(page, perPage, role, search),
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Hook to update user properties
 *
 * Admin-only mutation hook. Updates user role, name, or active status.
 * Automatically invalidates and refetches user data after successful update.
 *
 * @example
 * const updateUser = useUpdateUser();
 * updateUser.mutate({ id: userId, data: { role: 'admin' } });
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserUpdate }) => usersApi.updateUser(id, data),
    onSuccess: () => {
      // Invalidate both users list and current user (in case updating self)
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
};

/**
 * Hook to create invite link
 *
 * Admin-only mutation hook. Creates a time-limited invite token.
 * Automatically refetches invites list after successful creation.
 *
 * @example
 * const createInvite = useCreateInvite();
 * createInvite.mutate(
 *   { email: 'user@example.com', role: 'viewer' },
 *   { onSuccess: (invite) => storeInviteToken(invite.token) }
 * );
 */
export const useCreateInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteCreate) => usersApi.createInvite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
};

/**
 * Hook to fetch all invites
 *
 * Returns all invites (active, expired, and used) for display in settings.
 * Data is cached for 1 minute.
 *
 * @example
 * const { data: invitesData } = useInvites();
 * const activeInvites = invitesData?.items.filter(i => i.is_valid) || [];
 */
export const useInvites = () => {
  return useQuery({
    queryKey: ['invites'],
    queryFn: () => usersApi.getInvites(),
    staleTime: 1000 * 60, // 1 minute
  });
};

// ==================== FALLBACK AUTH HOOKS ====================

export const useCreateFallbackInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FallbackInviteCreate) => fallbackAuthApi.createFallbackInvite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
};

export const useRegenerateFallbackInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: number) => fallbackAuthApi.regenerateFallbackInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
};

export const useRevokeFallbackInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: number) => fallbackAuthApi.revokeFallbackInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
};

export const useActivateFallbackAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FallbackActivateRequest) => fallbackAuthApi.activateFallbackAccount(data),
    onSuccess: (tokenResponse) => {
      fallbackSession.setAuthToken(tokenResponse.accessToken);
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
};

export const useFallbackLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FallbackLoginRequest) => fallbackAuthApi.fallbackLogin(data),
    onSuccess: (tokenResponse) => {
      fallbackSession.setAuthToken(tokenResponse.accessToken);
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['audit-events'] });
    },
  });
};

export const useAuditEvents = (params?: AuditQueryParams, enabled = true) => {
  return useQuery({
    queryKey: ['audit-events', params],
    queryFn: () => fallbackAuthApi.getAuditEvents(params),
    staleTime: 1000 * 30,
    enabled,
  });
};

// ==================== FIELD OPTIONS (Admin-managed dropdowns) HOOKS ====================

export const useFieldOptionFields = () => {
  return useQuery({
    queryKey: ['field-option-fields'],
    queryFn: () => fieldOptionsApi.getFieldOptionFields(),
    staleTime: 1000 * 60 * 30, // 30 minutes - rarely changes
  });
};

export const useFieldOptions = (
  fieldKey: FieldOptionFieldKey | null,
  includeDeprecated: boolean
) => {
  return useQuery({
    queryKey: ['field-options', fieldKey, includeDeprecated],
    queryFn: () =>
      fieldOptionsApi.getFieldOptions(fieldKey as FieldOptionFieldKey, includeDeprecated),
    enabled: Boolean(fieldKey),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCreateFieldOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fieldKey,
      request,
    }: {
      fieldKey: FieldOptionFieldKey;
      request: FieldOptionCreateRequest;
    }) => fieldOptionsApi.createFieldOption(fieldKey, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['field-options', variables.fieldKey] });
    },
  });
};

export const useRenameFieldOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fieldKey,
      optionId,
      request,
    }: {
      fieldKey: FieldOptionFieldKey;
      optionId: number;
      request: FieldOptionRenameRequest;
    }) => fieldOptionsApi.renameFieldOption(fieldKey, optionId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['field-options', variables.fieldKey] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['filter-options'] });
      queryClient.invalidateQueries({ queryKey: ['field-counts'] });
    },
  });
};

export const useSetFieldOptionDeprecated = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fieldKey,
      optionId,
      request,
    }: {
      fieldKey: FieldOptionFieldKey;
      optionId: number;
      request: FieldOptionDeprecateRequest;
    }) => fieldOptionsApi.setFieldOptionDeprecated(fieldKey, optionId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['field-options', variables.fieldKey] });
    },
  });
};

// ==================== FEATURE FLAGS ====================

export interface AppFeatures {
  authWallEnabled: boolean;
}

const FALLBACK_APP_FEATURES: AppFeatures = {
  authWallEnabled: true,
};

export const useFeatures = () => {
  return useQuery<AppFeatures>({
    queryKey: ['app-features'],
    queryFn: async () => {
      const { apiClient } = await import('./client');
      try {
        const response = await apiClient.get('/');
        return response.data.features;
      } catch (error) {
        console.warn('Failed to fetch app features, using fallback', error);
        if (import.meta.env.DEV) {
          return {
            ...FALLBACK_APP_FEATURES,
            authWallEnabled: false,
          };
        }
        return FALLBACK_APP_FEATURES;
      }
    },
    retry: 0,
    staleTime: Infinity,
  });
};
